import { useState, useEffect, useCallback } from "react";
import { Clock, Loader2, Plus, X, Info, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import api from "../../lib/api";

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export default function ClosingReminderCard({ restaurantId }) {
  const [enabled, setEnabled] = useState(false);
  const [times, setTimes] = useState([]);
  const [lead, setLead] = useState(30);
  const [tz, setTz] = useState("");
  const [shiftTimes, setShiftTimes] = useState([]);
  const [newTime, setNewTime] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const c = await api.getClosingReminder(restaurantId);
      setEnabled(!!c.enabled);
      setTimes(c.times || []);
      setLead(c.lead_minutes ?? 30);
      setTz(c.timezone || "");
      setShiftTimes(c.shift_close_times || []);
    } catch (e) {
      toast.error(e.message || "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const addTime = () => {
    if (!TIME_RE.test(newTime)) { toast.error("Formato de hora inválido (HH:MM)"); return; }
    if (times.includes(newTime)) { toast.error("Esa hora ya está añadida"); return; }
    setTimes([...times, newTime].sort());
    setNewTime("");
  };

  const removeTime = (t) => setTimes(times.filter((x) => x !== t));

  const useShiftTimes = () => {
    setTimes([...new Set([...times, ...shiftTimes])].sort());
    toast.success("Horas de cierre cargadas desde tus turnos");
  };

  const save = async () => {
    if (enabled && times.length === 0) { toast.error("Añade al menos una hora de cierre"); return; }
    setSaving(true);
    try {
      const c = await api.updateClosingReminder(restaurantId, {
        enabled, times, lead_minutes: Number(lead) || 30,
      });
      setTimes(c.times || []);
      setLead(c.lead_minutes ?? 30);
      setTz(c.timezone || tz);
      toast.success("Recordatorio de cierre guardado");
    } catch (e) {
      toast.error(e.message || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <Card data-testid="closing-reminder-card">
      <CardContent className="p-5 space-y-5">
        <div className="flex items-start gap-4">
          <div className={`shrink-0 h-10 w-10 rounded-xl flex items-center justify-center ${enabled ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
            <Clock className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-foreground">Recordatorio de cierre de turno</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Recibe un aviso push {lead} min antes de cada cierre para preparar el arqueo de caja.
            </div>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="closing-reminder-toggle" />
        </div>

        {enabled && (
          <>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Horas de cierre</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {times.length === 0 && <span className="text-xs text-muted-foreground">Sin horas configuradas.</span>}
                {times.map((t) => (
                  <span key={t} data-testid={`closing-time-${t}`} className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-sm font-semibold">
                    {t}
                    <button onClick={() => removeTime(t)} data-testid={`closing-time-remove-${t}`} className="hover:text-primary/70">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} className="w-36" data-testid="closing-time-input" />
                <Button type="button" variant="outline" size="sm" onClick={addTime} data-testid="closing-time-add">
                  <Plus className="h-4 w-4 mr-1" /> Añadir
                </Button>
              </div>
              {shiftTimes.length > 0 && (
                <button type="button" onClick={useShiftTimes} data-testid="closing-use-shifts" className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-primary hover:underline">
                  <Wand2 className="h-3.5 w-3.5" /> Usar mis turnos actuales ({shiftTimes.join(", ")})
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-muted-foreground">Antelación (min)</label>
              <Input type="number" min={5} max={180} value={lead} onChange={(e) => setLead(e.target.value)} className="w-24" data-testid="closing-lead-input" />
            </div>

            {tz && (
              <div className="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-200 p-3 text-xs text-sky-900">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Zona horaria detectada por tu ubicación: <strong>{tz}</strong></span>
              </div>
            )}
          </>
        )}

        <Button onClick={save} disabled={saving} data-testid="closing-reminder-save" className="w-full">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Guardar
        </Button>
      </CardContent>
    </Card>
  );
}
